import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useSearchParams } from 'react-router-dom';
import DetailPanel from '../components/DetailPanel';
import { useAuth } from '../AuthContext';
import { OPERATIONS_TABS } from '../config/pages';

// ── Status maps ──────────────────────────────────────────────────────────────
const STATUS_MAP = {
  Draft:             { label:'In Progress',     color:'var(--accent)' },
  Rejected:          { label:'Rejected',         color:'#e84545' },
  'Pending Internal':{ label:'Internal Review', color:'#e87830' },
  'Pending Client':  { label:'Client Review',   color:'#6c58f0' },
  Approved:          { label:'Approved',         color:'var(--green)' },
};

const TAB_CONFIG = [
  { id:'production', label:'Production',        statuses:['Draft','Rejected'],   color:'var(--accent)' },
  { id:'internal',   label:'Internal Review',   statuses:['Pending Internal'],   color:'#e87830' },
  { id:'client',     label:'Client Review',     statuses:['Pending Client'],     color:'#6c58f0' },
  { id:'approved',   label:'Approved',          statuses:['Approved'],           color:'var(--green)' },
];

const EMPTY_MSG = {
  production:{ icon:'📹', msg:'No video work in production',             sub:'Work orders with a Video Works phase unlocked will appear here.' },
  internal:  { icon:'🔒', msg:'No videos pending internal review',       sub:'Submit videos for internal review to see them here.' },
  client:    { icon:'👔', msg:'No videos pending client review',         sub:'Internally approved videos will appear here.' },
  approved:  { icon:'✅', msg:'No approved videos yet',                  sub:'Client-approved videos will appear here.' },
};

const WORK_TYPES = { shoot:'🎬 Shoot Only', edit:'✂️ Edit Only', shoot_edit:'🎬✂️ Shoot + Edit' };

const PROD_STATUSES = ['Briefed','Assigned','Pre-Production','Shooting','Raw Delivered','Editing','Draft Ready'];
const PROD_COLORS   = {
  'Briefed':'#6b7280','Assigned':'#3b82f6','Pre-Production':'#8b5cf6',
  'Shooting':'#f59e0b','Raw Delivered':'#10b981','Editing':'#ec4899','Draft Ready':'#22c55e',
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function fmtDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'short', day:'2-digit', month:'short', year:'numeric' });
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   MAIN COMPONENT
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export default function VideoWorks() {
  const { user, can, canTab } = useAuth();
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const urlWo  = searchParams.get('wo');

  const allowedTabs = (OPERATIONS_TABS.videoworks || TAB_CONFIG).filter(
    t => !t.permissionKey || canTab(t.permissionKey)
  );

  const [tab, setTab]               = useState(urlTab && allowedTabs.find(t=>t.id===urlTab) ? urlTab : (allowedTabs[0]?.id || 'production'));
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeWOId, setActiveWOId] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [urlWoHandled, setUrlWoHandled] = useState(false);

  // ── Filters ──
  const [monthFilter, setMonthFilter]         = useState('');
  const [clientFilter, setClientFilter]       = useState('');
  const [campaignFilter, setCampaignFilter]   = useState('');
  const [searchTerm, setSearchTerm]           = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (monthFilter)    params.month            = monthFilter;
      if (clientFilter)   params.client_id        = clientFilter;
      if (campaignFilter) params.campaign_type_id  = campaignFilter;
      const r = await api.get('/video-works', { params });
      setItems(r.data.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [monthFilter, clientFilter, campaignFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-open work order from URL
  useEffect(() => {
    if (urlWoHandled || !urlWo || items.length === 0) return;
    const match = items.find(i => i.wo_number === urlWo || String(i.id) === urlWo);
    if (match) { setActiveWOId(match.id); setUrlWoHandled(true); }
  }, [items, urlWo, urlWoHandled]);

  // ── Derived data ──
  const tabCfg        = TAB_CONFIG.find(t => t.id === tab) || TAB_CONFIG[0];
  const filteredItems = items.filter(wo => {
    const vw = wo.video_work;
    const st = vw?.status || 'Draft';
    if (!tabCfg.statuses.includes(st)) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!((wo.wo_number||'').toLowerCase().includes(s) ||
            (wo.title||'').toLowerCase().includes(s) ||
            (wo.client_name||'').toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const activeWO = items.find(i => i.id === activeWOId);
  const activeVW = activeWO?.video_work;
  const counts   = {};
  TAB_CONFIG.forEach(t => {
    counts[t.id] = items.filter(wo => t.statuses.includes(wo.video_work?.status || 'Draft')).length;
  });

  // ── Unique clients & campaign types for filters ──
  const uniqueClients   = [...new Map(items.map(i => [i.client?.id, i.client]).filter(([k]) => k)).values()];
  const uniqueCampaigns = [...new Set(items.map(i => i.campaign_type).filter(Boolean))];

  // ── CRUD helpers ──
  const saveField = async (field, value) => {
    if (!activeWOId || saving) return;
    setSaving(true);
    try {
      await api.put(`/video-works/${activeWOId}`, { [field]: value });
      await fetchData();
    } catch (e) { alert(e.response?.data?.message || 'Save failed'); }
    setSaving(false);
  };

  const saveMultiple = async (data) => {
    if (!activeWOId || saving) return;
    setSaving(true);
    try {
      await api.put(`/video-works/${activeWOId}`, data);
      await fetchData();
    } catch (e) { alert(e.response?.data?.message || 'Save failed'); }
    setSaving(false);
  };

  const doAction = async (action) => {
    if (!activeWOId) return;
    setSaving(true);
    try {
      await api.post(`/video-works/${activeWOId}/${action}`);
      await fetchData();
    } catch (e) { alert(e.response?.data?.message || 'Action failed'); }
    setSaving(false);
  };

  const doReject = async (reason, fbVoice) => {
    if (!activeWOId) return;
    setSaving(true);
    try {
      await api.post(`/video-works/${activeWOId}/reject`, { reason, fb_voice_data: fbVoice });
      await fetchData();
    } catch (e) { alert(e.response?.data?.message || 'Action failed'); }
    setSaving(false);
  };

  // ── Render ──
  return (
    <div className="page-wrap" style={{ display:'flex', gap:0, height:'calc(100vh - 56px)' }}>
      {/* ── LEFT: List ── */}
      <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', borderRight:'1px solid var(--border)' }}>
        {/* Tabs */}
        <div style={{ display:'flex', gap:6, padding:'12px 16px 0', flexWrap:'wrap' }}>
          {allowedTabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding:'6px 14px', borderRadius:8, fontSize:'0.78rem', fontWeight:600,
                border: tab===t.id ? `2px solid ${t.color}` : '1px solid var(--border)',
                background: tab===t.id ? t.color+'18' : 'var(--bg2)',
                color: tab===t.id ? t.color : 'var(--text-muted)',
                cursor:'pointer',
              }}>
              {t.icon} {t.label}
              <span style={{ marginLeft:6, fontSize:'0.7rem', opacity:0.7 }}>({counts[t.id]||0})</span>
            </button>
          ))}
        </div>

        {/* Search & filters */}
        <div style={{ padding:'10px 16px', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <input placeholder="Search WO, title, client..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ flex:1, minWidth:160, padding:'6px 10px', borderRadius:6, border:'1px solid var(--border)',
              background:'var(--bg)', color:'var(--text)', fontSize:'0.8rem' }} />
          <select className="form-select" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
            style={{ fontSize:'0.78rem', padding:'5px 8px' }}>
            <option value="">All Clients</option>
            {uniqueClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
            style={{ fontSize:'0.78rem', padding:'5px 8px', borderRadius:6, border:'1px solid var(--border)',
              background:'var(--bg)', color:'var(--text)' }} />
        </div>

        {/* WO list */}
        <div style={{ flex:1, overflowY:'auto', padding:'0 16px 16px' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="card" style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>
              <div style={{ fontSize:'2rem', marginBottom:8 }}>{EMPTY_MSG[tab]?.icon || '📹'}</div>
              <div style={{ fontSize:'0.9rem', fontWeight:600 }}>{EMPTY_MSG[tab]?.msg}</div>
              <div style={{ fontSize:'0.8rem', marginTop:4 }}>{EMPTY_MSG[tab]?.sub}</div>
            </div>
          ) : (
            filteredItems.map(wo => (
              <WOCard key={wo.id} wo={wo} active={activeWOId===wo.id} onClick={() => setActiveWOId(wo.id)} />
            ))
          )}
        </div>
      </div>

      {/* ── RIGHT: Detail panel ── */}
      <div style={{ width:activeWOId ? 480 : 0, transition:'width 0.2s', overflow:'hidden', flexShrink:0 }}>
        {activeWO && (
          <VideoDetailPanel
            wo={activeWO}
            vw={activeVW}
            user={user}
            can={can}
            saving={saving}
            onSaveField={saveField}
            onSaveMultiple={saveMultiple}
            onAction={doAction}
            onReject={doReject}
            onClose={() => setActiveWOId(null)}
            onRefresh={fetchData}
          />
        )}
      </div>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   WO LIST CARD
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function WOCard({ wo, active, onClick }) {
  const vw   = wo.video_work;
  const st   = STATUS_MAP[vw?.status || 'Draft'] || STATUS_MAP.Draft;
  const prod = vw?.production_status || 'Briefed';
  const type = WORK_TYPES[vw?.work_type || 'shoot_edit'] || '🎬✂️ Shoot + Edit';

  return (
    <div className="card" onClick={onClick} style={{
      padding:'14px 16px', marginBottom:8, cursor:'pointer',
      borderLeft: active ? '3px solid var(--accent)' : '3px solid transparent',
      background: active ? 'var(--accent-light, rgba(99,102,241,0.06))' : undefined,
      transition:'all 0.15s',
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <span style={{ fontFamily:'Space Mono, monospace', fontSize:'0.8rem', fontWeight:700, color:'var(--accent)' }}>
              {wo.wo_number}
            </span>
            <span style={{ fontSize:'0.84rem', fontWeight:600, color:'var(--text)' }}>{wo.title}</span>
          </div>
          <div style={{ fontSize:'0.76rem', color:'var(--text-muted)', marginTop:4, display:'flex', gap:8, flexWrap:'wrap' }}>
            <span>{wo.client_name || '—'}</span>
            {wo.event_date && <span>· {fmtDateShort(wo.event_date)}</span>}
            <span>· {type}</span>
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
          <span style={{
            fontSize:'0.7rem', fontWeight:700, padding:'2px 8px', borderRadius:6,
            background: st.color + '18', color: st.color, whiteSpace:'nowrap',
          }}>{st.label}</span>
          <span style={{
            fontSize:'0.66rem', fontWeight:600, padding:'2px 6px', borderRadius:4,
            background: (PROD_COLORS[prod]||'#6b7280') + '18', color: PROD_COLORS[prod]||'#6b7280',
          }}>{prod}</span>
        </div>
      </div>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   DETAIL PANEL
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function VideoDetailPanel({ wo, vw, user, can, saving, onSaveField, onSaveMultiple, onAction, onReject, onClose, onRefresh }) {
  const [editMode, setEditMode]     = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Local form state
  const [form, setForm] = useState({});

  useEffect(() => {
    if (vw) {
      setForm({
        work_type:         vw.work_type || 'shoot_edit',
        brief:             vw.brief || '',
        concept:           vw.concept || '',
        location:          vw.location || '',
        shoot_date:        vw.shoot_date || '',
        shoot_time:        vw.shoot_time || '',
        external_name:     vw.external_name || '',
        external_phone:    vw.external_phone || '',
        external_email:    vw.external_email || '',
        equipment_notes:   vw.equipment_notes || '',
        requirements:      vw.requirements || '',
        raw_footage_link:  vw.raw_footage_link || '',
        draft_edit_link:   vw.draft_edit_link || '',
        final_link:        vw.final_link || '',
        notes:             vw.notes || '',
        production_status: vw.production_status || 'Briefed',
      });
    } else {
      setForm({ work_type:'shoot_edit', brief:'', concept:'', location:'', shoot_date:'', shoot_time:'',
        external_name:'', external_phone:'', external_email:'', equipment_notes:'', requirements:'',
        raw_footage_link:'', draft_edit_link:'', final_link:'', notes:'', production_status:'Briefed' });
    }
    setEditMode(!vw);
    setRejectMode(false);
    setRejectReason('');
  }, [vw, wo.id]);

  const handleSave = () => {
    onSaveMultiple(form);
    setEditMode(false);
  };

  const status       = vw?.status || 'Draft';
  const canApproveInt = can('action:approve:videowork:internal');
  const canApproveCl  = can('action:approve:videowork:client');
  const isClient      = user?.role === 'client';
  const showEdit      = !isClient && ['Draft','Rejected'].includes(status);

  const prodStatus = vw?.production_status || 'Briefed';
  const prodIdx    = PROD_STATUSES.indexOf(prodStatus);

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', background:'var(--bg)' }}>
      {/* Header */}
      <div style={{
        padding:'14px 16px', borderBottom:'1px solid var(--border)',
        display:'flex', justifyContent:'space-between', alignItems:'center',
      }}>
        <div>
          <span style={{ fontFamily:'Space Mono, monospace', fontSize:'0.82rem', fontWeight:700, color:'var(--accent)' }}>
            {wo.wo_number}
          </span>
          <span style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--text)', marginLeft:8 }}>{wo.title}</span>
        </div>
        <button onClick={onClose} style={{
          background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:'var(--text-muted)', padding:'4px 8px',
        }}>✕</button>
      </div>

      {/* Content */}
      <div style={{ flex:1, overflowY:'auto', padding:16 }}>
        {/* Status badges */}
        <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
          <StatusBadge label={STATUS_MAP[status]?.label || status} color={STATUS_MAP[status]?.color || '#666'} />
          <StatusBadge label={prodStatus} color={PROD_COLORS[prodStatus] || '#666'} />
          <StatusBadge label={WORK_TYPES[vw?.work_type || form.work_type] || 'Shoot + Edit'} color="#6366f1" />
        </div>

        {/* Production progress bar */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:'0.74rem', fontWeight:700, color:'var(--text)', marginBottom:6 }}>Production Progress</div>
          <div style={{ display:'flex', gap:2 }}>
            {PROD_STATUSES.map((ps, i) => (
              <div key={ps} title={ps} style={{
                flex:1, height:6, borderRadius:3,
                background: i <= prodIdx ? (PROD_COLORS[ps]||'#6b7280') : 'var(--border)',
                transition:'background 0.3s',
              }} />
            ))}
          </div>
          <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:3 }}>
            {prodIdx + 1} of {PROD_STATUSES.length} — {prodStatus}
          </div>
        </div>

        {/* WO Info */}
        <Section title="Work Order">
          <InfoRow label="Client" value={wo.client_name} />
          <InfoRow label="Event Date" value={fmtDateShort(wo.event_date)} />
          <InfoRow label="Campaign" value={wo.campaign_type} />
          {wo.platforms?.length > 0 && (
            <InfoRow label="Platforms" value={wo.platforms.join(', ')} />
          )}
        </Section>

        {/* Brief & Concept */}
        {editMode ? (
          <Section title="Brief & Concept">
            <Label>Work Type</Label>
            <select className="form-select" value={form.work_type}
              onChange={e => setForm({...form, work_type:e.target.value})}
              style={{ fontSize:'0.8rem', marginBottom:8 }}>
              <option value="shoot_edit">🎬✂️ Shoot + Edit</option>
              <option value="shoot">🎬 Shoot Only</option>
              <option value="edit">✂️ Edit Only</option>
            </select>
            <Label>Brief</Label>
            <textarea rows={3} value={form.brief} onChange={e => setForm({...form, brief:e.target.value})}
              style={taStyle} placeholder="Describe what needs to be done..." />
            <Label>Concept / Mood</Label>
            <textarea rows={2} value={form.concept} onChange={e => setForm({...form, concept:e.target.value})}
              style={taStyle} placeholder="Visual style, mood, references..." />
          </Section>
        ) : (
          <Section title="Brief & Concept">
            <InfoRow label="Work Type" value={WORK_TYPES[vw?.work_type] || '—'} />
            {vw?.brief && <InfoRow label="Brief" value={vw.brief} />}
            {vw?.concept && <InfoRow label="Concept" value={vw.concept} />}
          </Section>
        )}

        {/* Shoot Details (hide for edit-only) */}
        {(editMode ? form.work_type !== 'edit' : (vw?.work_type || 'shoot_edit') !== 'edit') && (
          editMode ? (
            <Section title="Shoot Details">
              <Label>Location</Label>
              <input value={form.location} onChange={e => setForm({...form, location:e.target.value})}
                style={inStyle} placeholder="Shoot location" />
              <div style={{ display:'flex', gap:8 }}>
                <div style={{ flex:1 }}>
                  <Label>Shoot Date</Label>
                  <input type="date" value={form.shoot_date} onChange={e => setForm({...form, shoot_date:e.target.value})}
                    style={inStyle} />
                </div>
                <div style={{ flex:1 }}>
                  <Label>Time</Label>
                  <input value={form.shoot_time} onChange={e => setForm({...form, shoot_time:e.target.value})}
                    style={inStyle} placeholder="e.g. 10 AM - 2 PM" />
                </div>
              </div>
              <Label>Equipment Notes</Label>
              <textarea rows={2} value={form.equipment_notes} onChange={e => setForm({...form, equipment_notes:e.target.value})}
                style={taStyle} placeholder="Camera, lighting, audio gear..." />
              <Label>Requirements</Label>
              <textarea rows={2} value={form.requirements} onChange={e => setForm({...form, requirements:e.target.value})}
                style={taStyle} placeholder="Permits, parking, power..." />
            </Section>
          ) : (
            (vw?.location || vw?.shoot_date) && (
              <Section title="Shoot Details">
                {vw?.location && <InfoRow label="Location" value={vw.location} />}
                {vw?.shoot_date && <InfoRow label="Date" value={fmtDateShort(vw.shoot_date)} />}
                {vw?.shoot_time && <InfoRow label="Time" value={vw.shoot_time} />}
                {vw?.equipment_notes && <InfoRow label="Equipment" value={vw.equipment_notes} />}
                {vw?.requirements && <InfoRow label="Requirements" value={vw.requirements} />}
              </Section>
            )
          )
        )}

        {/* Assignment */}
        {editMode ? (
          <Section title="Assignment (External)">
            <Label>Videographer Name</Label>
            <input value={form.external_name} onChange={e => setForm({...form, external_name:e.target.value})}
              style={inStyle} placeholder="Name" />
            <div style={{ display:'flex', gap:8 }}>
              <div style={{ flex:1 }}>
                <Label>Phone</Label>
                <input value={form.external_phone} onChange={e => setForm({...form, external_phone:e.target.value})}
                  style={inStyle} placeholder="Phone" />
              </div>
              <div style={{ flex:1 }}>
                <Label>Email</Label>
                <input value={form.external_email} onChange={e => setForm({...form, external_email:e.target.value})}
                  style={inStyle} placeholder="Email" />
              </div>
            </div>
          </Section>
        ) : (
          (vw?.assigned_user || vw?.external_name) && (
            <Section title="Assignment">
              {vw?.assigned_user && <InfoRow label="In-house" value={vw.assigned_user} />}
              {vw?.external_name && <InfoRow label="Videographer" value={vw.external_name} />}
              {vw?.external_phone && <InfoRow label="Phone" value={vw.external_phone} />}
              {vw?.external_email && <InfoRow label="Email" value={vw.external_email} />}
            </Section>
          )
        )}

        {/* Shot List */}
        {vw?.shot_list?.length > 0 && !editMode && (
          <Section title="Shot List">
            {vw.shot_list.map((shot, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 0', fontSize:'0.8rem' }}>
                <span style={{ fontSize:'1rem' }}>{shot.done ? '✅' : '⬜'}</span>
                <span style={{ color: shot.done ? 'var(--text-muted)' : 'var(--text)',
                  textDecoration: shot.done ? 'line-through' : 'none' }}>{shot.text}</span>
              </div>
            ))}
            <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:4 }}>
              {vw.shot_list.filter(s => s.done).length} of {vw.shot_list.length} completed
            </div>
          </Section>
        )}

        {/* Delivery Links */}
        {editMode ? (
          <Section title="Delivery Links">
            {form.work_type !== 'edit' && (
              <>
                <Label>Raw Footage Link</Label>
                <input value={form.raw_footage_link} onChange={e => setForm({...form, raw_footage_link:e.target.value})}
                  style={inStyle} placeholder="Google Drive / Dropbox link" />
              </>
            )}
            <Label>Draft Edit Link</Label>
            <input value={form.draft_edit_link} onChange={e => setForm({...form, draft_edit_link:e.target.value})}
              style={inStyle} placeholder="Draft video link for review" />
            <Label>Final Video Link</Label>
            <input value={form.final_link} onChange={e => setForm({...form, final_link:e.target.value})}
              style={inStyle} placeholder="Final approved video link" />
          </Section>
        ) : (
          (vw?.raw_footage_link || vw?.draft_edit_link || vw?.final_link) && (
            <Section title="Delivery Links">
              {vw?.raw_footage_link && <LinkRow label="Raw Footage" url={vw.raw_footage_link} />}
              {vw?.draft_edit_link && <LinkRow label="Draft Edit" url={vw.draft_edit_link} />}
              {vw?.final_link && <LinkRow label="Final Video" url={vw.final_link} />}
            </Section>
          )
        )}

        {/* Production Status (edit mode) */}
        {editMode && (
          <Section title="Production Status">
            <select className="form-select" value={form.production_status}
              onChange={e => setForm({...form, production_status:e.target.value})}
              style={{ fontSize:'0.8rem' }}>
              {PROD_STATUSES.map(ps => <option key={ps} value={ps}>{ps}</option>)}
            </select>
          </Section>
        )}

        {/* Notes */}
        {editMode ? (
          <Section title="Notes">
            <textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})}
              style={taStyle} placeholder="Internal notes..." />
          </Section>
        ) : (
          vw?.notes && <Section title="Notes"><div style={{ fontSize:'0.82rem', color:'var(--text)', whiteSpace:'pre-wrap' }}>{vw.notes}</div></Section>
        )}

        {/* References */}
        {vw?.references?.length > 0 && !editMode && (
          <Section title="References">
            {vw.references.map((ref, i) => (
              <div key={i} style={{ marginBottom:4 }}>
                {typeof ref === 'string' ? (
                  <a href={ref} target="_blank" rel="noreferrer" style={{ fontSize:'0.8rem', color:'var(--accent)', wordBreak:'break-all' }}>{ref}</a>
                ) : ref.url ? (
                  <a href={ref.url} target="_blank" rel="noreferrer" style={{ fontSize:'0.8rem', color:'var(--accent)', wordBreak:'break-all' }}>{ref.label || ref.url}</a>
                ) : (
                  <span style={{ fontSize:'0.8rem', color:'var(--text)' }}>{ref.label || JSON.stringify(ref)}</span>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* Rejection reason */}
        {vw?.rejection_reason && (
          <Section title="Revision Feedback">
            <div style={{ padding:10, background:'#fef2f2', borderRadius:8, border:'1px solid #fecaca', fontSize:'0.82rem', color:'#991b1b' }}>
              {vw.rejection_reason}
            </div>
          </Section>
        )}

        {/* Version history */}
        {vw?.versions?.length > 0 && (
          <Section title="Version History">
            {[...vw.versions].reverse().map((v, i) => (
              <div key={i} style={{ padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:'0.78rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontWeight:600 }}>{v.version} — {v.action}</span>
                  <span style={{ color:'var(--text-muted)' }}>{fmtDate(v.at)}</span>
                </div>
                <div style={{ color:'var(--text-muted)' }}>by {v.by}{v.stage ? ` (${v.stage})` : ''}</div>
                {v.rejectionReason && <div style={{ color:'#e84545', marginTop:2 }}>Reason: {v.rejectionReason}</div>}
              </div>
            ))}
          </Section>
        )}

        {/* Reject panel */}
        {rejectMode && (
          <Section title="Request Revision">
            <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              style={taStyle} placeholder="Describe what needs to change..." />
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button className="btn" onClick={() => { onReject(rejectReason, null); setRejectMode(false); setRejectReason(''); }}
                disabled={saving} style={{ background:'#e84545', color:'#fff', fontSize:'0.8rem' }}>
                {saving ? 'Sending...' : 'Send Revision Request'}
              </button>
              <button className="btn" onClick={() => setRejectMode(false)}
                style={{ background:'var(--bg2)', color:'var(--text)', fontSize:'0.8rem' }}>Cancel</button>
            </div>
          </Section>
        )}
      </div>

      {/* Footer actions */}
      <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', display:'flex', gap:8, flexWrap:'wrap' }}>
        {showEdit && !editMode && (
          <button className="btn" onClick={() => setEditMode(true)}
            style={{ background:'var(--accent)', color:'#fff', fontSize:'0.8rem' }}>Edit</button>
        )}
        {editMode && (
          <>
            <button className="btn" onClick={handleSave} disabled={saving}
              style={{ background:'var(--accent)', color:'#fff', fontSize:'0.8rem' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            {vw && <button className="btn" onClick={() => setEditMode(false)}
              style={{ background:'var(--bg2)', color:'var(--text)', fontSize:'0.8rem' }}>Cancel</button>}
          </>
        )}
        {!editMode && showEdit && vw && (
          <button className="btn" onClick={() => onAction('submit-internal')} disabled={saving}
            style={{ background:'#e87830', color:'#fff', fontSize:'0.8rem' }}>
            Submit for Review
          </button>
        )}
        {status === 'Pending Internal' && canApproveInt && !rejectMode && (
          <>
            <button className="btn" onClick={() => onAction('approve-internal')} disabled={saving}
              style={{ background:'var(--green)', color:'#fff', fontSize:'0.8rem' }}>Approve (Internal)</button>
            <button className="btn" onClick={() => setRejectMode(true)}
              style={{ background:'#e84545', color:'#fff', fontSize:'0.8rem' }}>Request Revision</button>
          </>
        )}
        {status === 'Pending Client' && canApproveCl && !rejectMode && (
          <>
            <button className="btn" onClick={() => onAction('approve-client')} disabled={saving}
              style={{ background:'var(--green)', color:'#fff', fontSize:'0.8rem' }}>Approve (Client)</button>
            <button className="btn" onClick={() => setRejectMode(true)}
              style={{ background:'#e84545', color:'#fff', fontSize:'0.8rem' }}>Request Revision</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   UI HELPERS
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function Section({ title, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:'0.74rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>{title}</div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display:'flex', gap:8, padding:'3px 0', fontSize:'0.82rem' }}>
      <span style={{ color:'var(--text-muted)', minWidth:90, flexShrink:0 }}>{label}:</span>
      <span style={{ color:'var(--text)', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{value || '—'}</span>
    </div>
  );
}

function LinkRow({ label, url }) {
  return (
    <div style={{ display:'flex', gap:8, padding:'4px 0', fontSize:'0.82rem', alignItems:'center' }}>
      <span style={{ color:'var(--text-muted)', minWidth:90, flexShrink:0 }}>{label}:</span>
      <a href={url} target="_blank" rel="noreferrer" style={{ color:'var(--accent)', wordBreak:'break-all', display:'flex', alignItems:'center', gap:4 }}>
        🔗 {url.length > 50 ? url.substring(0, 50) + '...' : url}
      </a>
    </div>
  );
}

function StatusBadge({ label, color }) {
  return (
    <span style={{
      fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:6,
      background: color + '18', color,
    }}>{label}</span>
  );
}

function Label({ children }) {
  return <div style={{ fontSize:'0.74rem', fontWeight:600, color:'var(--text-muted)', marginBottom:3, marginTop:8 }}>{children}</div>;
}

const inStyle = {
  width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)',
  background:'var(--bg)', color:'var(--text)', fontSize:'0.82rem', marginBottom:4,
};

const taStyle = {
  ...inStyle, resize:'vertical', fontFamily:'inherit',
};
